import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataGrid,
  GridColDef,
  GridValueGetter,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { AnyModule, ModuleListParams } from './module.types';
import {
  activateUserModule,
  deactivateUserModule,
  deletePublicModule,
  fetchModules,
} from './module.api';
import { useAccessToken } from '@features/user';

// Format timestamp to readable date/time
const formatDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleString();
};

interface ModuleListTableProps {
  onCreateModule: () => void;
  onEditModule: (module: AnyModule) => void;
  userId?: string; // Optional user ID to filter modules
}

export const ModuleListTable: React.FC<ModuleListTableProps> = ({
  onCreateModule,
  onEditModule,
  userId,
}) => {
  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<AnyModule[]>([]);
  const [totalModules, setTotalModules] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Filter states
  const [search, setSearch] = useState('');
  const [moduleTypeFilter, setModuleTypeFilter] = useState<string>(userId ? 'user' : 'public');
  const [isPublicFilter, setIsPublicFilter] = useState<string>('all');
  const [isMetaModuleFilter, setIsMetaModuleFilter] = useState<string>('all');
  const [isMarkdownFilter, setIsMarkdownFilter] = useState<string>('all');
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [isDeactivatedFilter, setIsDeactivatedFilter] = useState<string>('all');

  // Columns definition for the DataGrid
  const columns: GridColDef[] = [
    {
      field: 'module_name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (_, row) => {
        return row.public_name || row.module_name;
      },
    },
    { field: 'module_id', headerName: 'ID', width: 100 },
    { field: 'module_version', headerName: 'Version', width: 80 },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      valueGetter: (_, row) => {
        return row._moduleType === 'public' ? 'Public' : 'User';
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.value as string}
            color={params.value === 'Public' ? 'primary' : 'secondary'}
            size="small"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: (_, row) => {
        if (row._moduleType === 'public') {
          return row.public ? 'Public' : 'Private';
        } else {
          return row.deactivated ? 'Deactivated' : 'Active';
        }
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.value as string}
            color={params.value === 'Deactivated' ? 'error' : 'success'}
            size="small"
          />
        );
      },
    },
    {
      field: 'modified_on',
      headerName: 'Last Modified',
      width: 180,
      valueFormatter: (_, row) => formatDate(row.modified_on),
    },
    {
      field: 'created_on',
      headerName: 'Created On',
      width: 180,
      valueFormatter: (_, row) => formatDate(row.created_on),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const module = params.row as AnyModule;

        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEditModule(module)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {module._moduleType === 'public' ? (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteModule(module.module_id, module.module_version)}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            ) : module.deactivated ? (
              <Tooltip title="Activate">
                <IconButton
                  size="small"
                  onClick={() => handleActivateModule(module.module_id, module.module_version)}
                >
                  <CheckCircleIcon fontSize="small" color="success" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Deactivate">
                <IconButton
                  size="small"
                  onClick={() => handleDeactivateModule(module.module_id, module.module_version)}
                >
                  <BlockIcon fontSize="small" color="warning" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const loadModules = async () => {
    setLoading(true);

    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.error('No access token available');
      setLoading(false);
      return;
    }

    try {
      // Construct filter parameters
      const params: ModuleListParams = {
        page: paginationModel.page + 1, // Convert 0-based to 1-based
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      };

      // If userId is provided, only show user modules for that user
      if (userId) {
        params.filterByType = 'user';
        params.filterByUserId = userId;
      } else {
        // Default to showing only public modules
        params.filterByType = 'public';
      }

      // Add other filters (only if relevant to selected module type)
      if (moduleTypeFilter === 'public' || moduleTypeFilter === 'all') {
        if (isPublicFilter !== 'all') {
          params.filterByIsPublic = isPublicFilter === 'true';
        }
      }

      if (isMetaModuleFilter !== 'all') {
        params.filterByMetaModule = isMetaModuleFilter === 'true';
      }

      if (isMarkdownFilter !== 'all') {
        params.filterByMarkdown = isMarkdownFilter === 'true';
      }

      if (moduleTypeFilter === 'user' || moduleTypeFilter === 'all') {
        if (languageFilter !== 'all') {
          params.filterByLanguage = languageFilter;
        }

        if (isDeactivatedFilter !== 'all') {
          params.filterByDeactivated = isDeactivatedFilter === 'true';
        }
      }

      const response = await fetchModules(params, accessToken);
      setModules(response.data);
      setTotalModules(response.total);
    } catch (error) {
      console.error('Failed to load modules:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle module deletion
  const handleDeleteModule = async (moduleId: string, moduleVersion: number) => {
    if (
      window.confirm('Are you sure you want to delete this module? This action cannot be undone.')
    ) {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('No access token available');
          return;
        }
        await deletePublicModule(moduleId, moduleVersion, accessToken);
        // Refresh the list
        loadModules();
      } catch (error) {
        console.error('Failed to delete module:', error);
      }
    }
  };

  // Handle module deactivation
  const handleDeactivateModule = async (moduleId: string, moduleVersion: number) => {
    if (window.confirm('Are you sure you want to deactivate this module?')) {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('No access token available');
          return;
        }
        await deactivateUserModule(moduleId, moduleVersion, accessToken);
        // Refresh the list
        loadModules();
      } catch (error) {
        console.error('Failed to deactivate module:', error);
      }
    }
  };

  // Handle module activation
  const handleActivateModule = async (moduleId: string, moduleVersion: number) => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        console.error('No access token available');
        return;
      }
      await activateUserModule(moduleId, moduleVersion, accessToken);
      // Refresh the list
      loadModules();
    } catch (error) {
      console.error('Failed to activate module:', error);
    }
  };

  // Effect to load modules when pagination or filters change
  useEffect(() => {
    loadModules();
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    search,
    moduleTypeFilter,
    isPublicFilter,
    isMetaModuleFilter,
    isMarkdownFilter,
    languageFilter,
    isDeactivatedFilter,
  ]);

  // Handle filter changes
  const handleModuleTypeChange = (event: SelectChangeEvent) => {
    setModuleTypeFilter(event.target.value);
  };

  const handleIsPublicChange = (event: SelectChangeEvent) => {
    setIsPublicFilter(event.target.value);
  };

  const handleMetaModuleChange = (event: SelectChangeEvent) => {
    setIsMetaModuleFilter(event.target.value);
  };

  const handleMarkdownChange = (event: SelectChangeEvent) => {
    setIsMarkdownFilter(event.target.value);
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setLanguageFilter(event.target.value);
  };

  const handleDeactivatedChange = (event: SelectChangeEvent) => {
    setIsDeactivatedFilter(event.target.value);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h2">
          {userId ? 'User Modules' : 'Public Modules'}
        </Typography>
        <Button variant="contained" color="primary" onClick={onCreateModule}>
          Create New Module
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />

          {/* Only show module type filter when viewing all modules (no userId) */}
          {!userId && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Module Type</InputLabel>
              <Select
                value={moduleTypeFilter}
                label="Module Type"
                onChange={handleModuleTypeChange}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="public">Public Modules</MenuItem>
                <MenuItem value="user">User Modules</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Show Public filter only for public modules */}
          {!userId && (moduleTypeFilter === 'public' || moduleTypeFilter === 'all') && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Public Status</InputLabel>
              <Select value={isPublicFilter} label="Public Status" onChange={handleIsPublicChange}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Public</MenuItem>
                <MenuItem value="false">Private</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Meta Module</InputLabel>
            <Select
              value={isMetaModuleFilter}
              label="Meta Module"
              onChange={handleMetaModuleChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Markdown</InputLabel>
            <Select value={isMarkdownFilter} label="Markdown" onChange={handleMarkdownChange}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          {/* Show language and deactivated filters only for user modules */}
          {(userId || moduleTypeFilter === 'user' || moduleTypeFilter === 'all') && (
            <>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Language</InputLabel>
                <Select value={languageFilter} label="Language" onChange={handleLanguageChange}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  {/* Add more languages as needed */}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isDeactivatedFilter}
                  label="Status"
                  onChange={handleDeactivatedChange}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="false">Active</MenuItem>
                  <MenuItem value="true">Deactivated</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </Box>

      {/* DataGrid for displaying modules */}
      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={modules}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalModules}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={row => `${row.module_id}-${row.module_version}-${row._moduleType}`}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </div>
    </Paper>
  );
};

export default ModuleListTable;
